import {
  IonButton,
  IonFooter,
  IonIcon,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonToolbar,
  ToastButton,
  useIonToast,
} from '@ionic/react'
import { ReportExtract, ValidationErrorResponse } from 'domain/usecases/add-new-report'
import { ReportsService } from 'infra/services/reports-service'
import { cloudUploadSharp, documentText } from 'ionicons/icons'
import { DefaultInput, ModalTitle, Typeahead } from 'presentation/components'
import React, { useEffect, useRef, useState } from 'react'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { ModalNewReportSteps } from '../modal-new-report'
import { modalNewReportAtom, reportPatientAtom } from '../modal-new-report-atom'
import { OncologistTypeaheadItem } from './confirm-patient'
import { ClinicsService } from 'infra/services/clinics-service'
import { UserInfo, UserRole } from 'infra/services/usecases/get-user-info'
import { TypeaheadItem } from 'presentation/components/typeahead/typeahead'

interface NewReportProps {
  reportsService: ReportsService
  onCancel: () => void
  onDidUpload: (params: { info: ReportExtract; step: ModalNewReportSteps }) => void
  onUploadError: (info: ValidationErrorResponse) => void
  clinicsService: ClinicsService
  userInfo: UserInfo
  isPatientAlreadyConfirmed: boolean
}

type NewReportFile = { file: File; fileName: string; lab: string }

const NewReport: React.FC<NewReportProps> = (props) => {
  const labOptions = [
    { label: 'Foundation', value: 'foundation_one' },
    { label: 'Tempus', value: 'tempus' },
    { label: 'Foundation Liquid', value: 'foundation_liquid' },
    { label: 'Guardant', value: 'guardant' }
  ]

  const inputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<NewReportFile>({} as NewReportFile)
  const [isUploading, setIsUploading] = useState(false)
  const [presentToast] = useIonToast()
  const setReportExtract = useSetRecoilState(modalNewReportAtom)
  const [confirmPatient, setConfirmPatient] = useRecoilState(reportPatientAtom)
  const [oncologists, setOncologists] = useState<OncologistTypeaheadItem[]>([])
  const [isLoadingOncologists, setIsLoadingOncologists] = useState(false)

  const getFile: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const maxFileSizeMb = 20
    const file = e?.target?.files ? e?.target?.files[0] : null
    const toMb = (n: number) => n / 1024 / 1024

    if (!file) return showToastError('Invalid file, please try again!')
    if (toMb(file.size) > maxFileSizeMb) return showToastError('We only accept PDF files of up to 20Mb')

    setSelectedFile((s) => ({ ...s, file, fileName: file.name }))
  }

  useEffect(() => {
    if (props.userInfo.role === UserRole.Oncologist) {
      setConfirmPatient({
        ...confirmPatient,
        oncologist: props.userInfo.name,
        oncologistId: props.userInfo.id as string,
      })
    }
  }, [props.isPatientAlreadyConfirmed, props.userInfo.id])

  const handleUpload = () => {
    setIsUploading(true)
    props.reportsService
      .addNewReport({ lab: selectedFile.lab })
      .then((report) => {

        const reportExtract = report as ReportExtract
        props.reportsService.uploadReportPDF(reportExtract.presigned_url.presigned_url, selectedFile.file).then(() => {
          const patientExists = reportExtract.patient && reportExtract.patient.exists
          setReportExtract({ reportExtract: reportExtract })

          if (patientExists) {
            setConfirmPatient({
              MRN: reportExtract.patient.MRN,
              name: reportExtract.patient.name,
              reportId: reportExtract.report_id,
              oncologist: props.isPatientAlreadyConfirmed ? confirmPatient.oncologist : '',
              oncologistId: props.isPatientAlreadyConfirmed ? confirmPatient.oncologistId : '',
              oncologistError: props.isPatientAlreadyConfirmed ? confirmPatient.oncologistError : '',
            })
          }

          setIsUploading(false)

          props.onDidUpload({
            info: { ...reportExtract },
            step: patientExists ? 'confirm-patient' : 'confirm-new-patient',
          })
        })
      })
      .catch((error) => {
        setIsUploading(false)
        props.onUploadError(error as ValidationErrorResponse)
        showToastError(
          `An error occurred while trying to upload this report. 
        Please try it again later or contact the support team: support@oncorxinsights.com `,
          [
            {
              text: 'Contact',
              handler: () => {
                window.location.href = 'mailto:support@oncorxinsights.com'
              },
            },
          ]
        )
      })
  }

  const getOncologists = (search?: string) => {
    setIsLoadingOncologists(true)
    props.clinicsService
      .getOncologists({
        clinic_id: props.userInfo.clinic,
        page: 1,
        size: 10,
        search: search || ''
      })
      .then((result) => {
        const newItems = result.items.map((item) => ({ label: item?.name, value: item?.user_id, email: item?.email }))
        setOncologists(newItems)
      })
      .catch(() => setOncologists([]))
      .finally(() => setIsLoadingOncologists(false))
  }

  const showToastError = (message: string, buttons?: ToastButton[]) => {
    presentToast({
      message: message,
      duration: 5000,
      color: 'danger',
      buttons,
    })
  }

  return (
    <>
      <section className="ion-padding new-report-section">
        <ModalTitle label="New Report" icon={documentText} />

        {
          (props.userInfo.role === UserRole.MedicalAssistant && props.isPatientAlreadyConfirmed) && (
            <Typeahead
              className="typeahead-item"
              label="Oncologist"
              placeholder="Select an oncologist..."
              options={oncologists}
              isLoading={isLoadingOncologists}
              value={{ label: confirmPatient.oncologist, value: confirmPatient.oncologist }}
              onMenuOpen={() => getOncologists()}
              onTypeaheadDebounce={(search) => getOncologists(search)}
              onChange={(oncologist) =>
                setConfirmPatient((s) => ({
                  ...s,
                  oncologist: (oncologist as TypeaheadItem)?.label as string,
                  oncologistId: (oncologist as TypeaheadItem)?.value as string,
                }))
              }
            />
          )
        }

        <IonItem className="lab-selection">
          <IonLabel position="stacked">Lab</IonLabel>
          <IonSelect
            placeholder={'Select the lab'}
            onIonChange={(e) => setSelectedFile((s) => ({ ...s, lab: e.target.value }))}
          >
            {labOptions.map((option, index) => (
              <IonSelectOption key={index} value={option.value}>
                {option.label}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>

        <DefaultInput
          name="fileName"
          state={selectedFile}
          value={selectedFile.fileName}
          setState={setSelectedFile}
          labelText="File"
          placeholder="Max size for PDF: 20mb"
          notEditable
        />



        <input
          id="pdf-input"
          ref={inputRef}
          type="file"
          className="ion-input"
          accept="application/pdf"
          onChange={getFile}
        />

        <IonButton className="upload-button" fill="outline" expand="block" onClick={() => inputRef?.current?.click()}>
          <IonIcon icon={cloudUploadSharp} slot="start" />
          Upload File
        </IonButton>
      </section>

      <IonFooter>
        <IonToolbar>
          <IonButton slot="end" fill="outline" onClick={() => props.onCancel()}>
            Cancel
          </IonButton>
          <IonButton
            slot="end"
            onClick={handleUpload}
            disabled={!selectedFile.lab || !selectedFile.fileName || 
              ( props.userInfo.role === UserRole.MedicalAssistant && props.isPatientAlreadyConfirmed && !confirmPatient.oncologistId) || isUploading}
          >
            {isUploading ? <IonSpinner /> : <>Next</>}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </>
  )
}

export default NewReport
